/* eslint-disable import/prefer-default-export */
// eslint-disable-next-line import/no-extraneous-dependencies
import axios, { AxiosInstance } from 'axios';
import {
  getAuthToken,
  isForceClearLogout,
  setAuthToken,
  getRefreshToken,
  clearRefreshToken,
  setRefreshToken,
  setTokenExpiry,
  clearTokenExpiry,
  isTokenExpiringSoon,
  syncProfileFromToken,
} from '@/application/helpers/storage';
import { AlertCustom } from './alert';
import { STORAGE_KEYS } from './storageKeys';
import {
  handleError,
  notifyTokenBlacklisted,
  runNotFoundSideEffects,
  runServerErrorSideEffects,
} from './apiErrorSideEffects';
import apiConfig from '../config/api';
import i18next from 'i18next';

export type ApiBaseUrlKey =
  | 'API_BASE_URL'
  | 'AUTH_API_BASE_URL'
  | 'FINANCE_API_BASE_URL'
  | 'CUSTOMER_CREDIT_API_BASE_URL'
  | 'ORDER_API_BASE_URL'
  | 'GAME_CATALOG_API_BASE_URL'
  | 'RAZER_GOLD_WALLET_API_BASE_URL'
  | 'CRYPTO_PAYMENT_API_BASE_URL'
  | 'CUSTOMER_AUTH_API_BASE_URL'
  | 'CMS_API_BASE_URL';

export const apiBaseUrlMap: Record<ApiBaseUrlKey, string> = {
  API_BASE_URL: apiConfig.API_BASE_URL,
  AUTH_API_BASE_URL: apiConfig.AUTH_API_BASE_URL,
  FINANCE_API_BASE_URL: apiConfig.FINANCE_API_BASE_URL,
  CUSTOMER_CREDIT_API_BASE_URL: apiConfig.CUSTOMER_CREDIT_API_BASE_URL,
  ORDER_API_BASE_URL: apiConfig.ORDER_API_BASE_URL,
  GAME_CATALOG_API_BASE_URL: apiConfig.GAME_CATALOG_API_BASE_URL,
  RAZER_GOLD_WALLET_API_BASE_URL: apiConfig.RAZER_GOLD_WALLET_API_BASE_URL,
  CRYPTO_PAYMENT_API_BASE_URL: apiConfig.CRYPTO_PAYMENT_API_BASE_URL,
  CUSTOMER_AUTH_API_BASE_URL: apiConfig.CUSTOMER_AUTH_API_BASE_URL,
  CMS_API_BASE_URL: apiConfig.CMS_API_BASE_URL,
};

const resolveBaseUrl = (
  baseURL?: string,
  baseUrlKey?: ApiBaseUrlKey,
  fallback: string = apiConfig.API_BASE_URL
) => {
  if (baseURL) return baseURL;
  if (!baseUrlKey) return fallback;
  return apiBaseUrlMap[baseUrlKey] || fallback;
};

/**
 * Creates an axios instance with base URL from config.
 * @param options - baseURL, baseUrlKey, or custom headers
 * @returns Configured AxiosInstance
 */
export const createAxiosInstance = ({
  baseURL,
  baseUrlKey,
  headers,
}: {
  baseURL?: string;
  baseUrlKey?: ApiBaseUrlKey;
  headers?: Record<string, string>;
} = {}): AxiosInstance =>
  axios.create({
    baseURL: resolveBaseUrl(baseURL, baseUrlKey, apiConfig.API_BASE_URL),
    withCredentials: true, // send HttpOnly cookies on cross-origin requests
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
  });

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshNetworkFailTime = 0;
const REFRESH_NETWORK_FAIL_COOLDOWN_MS = 30_000;

const isTokenBlacklisted = (status?: number, code?: unknown, data?: unknown) =>
  status === 403 &&
  (code === 'TOKEN_BLACKLISTED' ||
    (typeof data === 'string' && data.trim().toLowerCase() === 'forbidden'));

/**
 * Single entry-point for token refresh across all API systems (axios interceptors + fetch APIRequest).
 * Ensures only one refresh request is in-flight at any time via shared `refreshPromise`.
 * Skips refresh for 30s after a network/CORS failure to prevent request storms.
 */
const ensureTokenRefreshed = (): Promise<boolean> => {
  if (lastRefreshNetworkFailTime && Date.now() - lastRefreshNetworkFailTime < REFRESH_NETWORK_FAIL_COOLDOWN_MS) {
    return Promise.resolve(false);
  }
  if (!refreshPromise) {
    refreshPromise = tryRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Avoid stacking duplicate interceptors on the same axios instance (e.g. Vite/Webpack HMR re-executing request modules).
 */
const axiosInstancesWithAuthInterceptors = new WeakSet<AxiosInstance>();

/**
 * Attaches request/response interceptors for auth token and refresh logic.
 * @param instance - Axios instance to attach interceptors to
 */
export const attachAuthInterceptors = (instance: AxiosInstance) => {
  if (axiosInstancesWithAuthInterceptors.has(instance)) {
    return;
  }
  axiosInstancesWithAuthInterceptors.add(instance);

  instance.interceptors.request.use(
    async (config) => {
      const token = getAuthToken();
      const refreshToken = getRefreshToken();
      const requestUrl = config.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/');
      if (token && refreshToken && isTokenExpiringSoon() && !isAuthEndpoint) {
        try {
          await ensureTokenRefreshed();
        } catch (error) {
          console.error('Failed to refresh token:', error);
        }
      }
      // Authorization header removed — browser sends HttpOnly cookie automatically via withCredentials

      const currentLanguage = i18next.language || 'en';
      config.headers['Accept-Language'] = currentLanguage;

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const requestUrl = originalRequest?.url || '';
      const responseStatus = error.response?.status;
      const responseData = error.response?.data;
      const responseCode = responseData?.code;

      if (isTokenBlacklisted(responseStatus, responseCode, responseData)) {
        notifyTokenBlacklisted();
        return Promise.reject(error);
      }

      if (responseStatus === 404) {
        if (!originalRequest?.skipNotFoundNavigation) {
          runNotFoundSideEffects();
        }
        return Promise.reject(error);
      }

      if (responseStatus === 500) {
        runServerErrorSideEffects({ error, originalRequest });
        return Promise.reject(error);
      }

      // Handle 403 permission denied: refresh token to sync permissions, then redirect to home
      if (
        responseStatus === 403 &&
        !isTokenBlacklisted(responseStatus, responseCode, responseData) &&
        !originalRequest._retry403
      ) {
        // Login endpoint 403 means bad credentials / suspended account — let the
        // caller's catch block handle it rather than doing a full-page redirect.
        if (requestUrl.includes('/auth/login')) {
          return Promise.reject(error);
        }
        originalRequest._retry403 = true;
        try {
          // Refresh token to get new JWT with current permissions from backend
          await ensureTokenRefreshed();
        } catch {
          // Refresh failed — permissions stay stale, still redirect
        }
        // Redirect to home — route guards will re-evaluate with fresh permissions
        window.location.href = '/';
        return Promise.reject(error);
      }

      const isAuthFlowRequest =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/2fa/verify');
      if (responseStatus === 401 && !originalRequest._retry) {
        if (!getAuthToken()) {
          if (!isAuthFlowRequest) {
            isForceClearLogout();
          }
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          const refreshResult = await ensureTokenRefreshed();
          if (refreshResult) {
            // Cookie is updated by refresh — just retry the request as-is
            return instance(originalRequest);
          }
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
        }
        // Only force logout if refresh token was cleared (i.e. backend rejected it)
        // Network errors keep refresh token → user can retry on next request
        if (!getRefreshToken()) {
          isForceClearLogout();
        }
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
};

// Helper function to refresh access token
async function tryRefreshToken(): Promise<boolean> {
  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return false;
    }

    const response = await createAxiosInstance({
      baseUrlKey: 'AUTH_API_BASE_URL',
    }).post('/auth/refresh', {}); // refresh token is sent via HttpOnly cookie (withCredentials)

    // Backend returns tokens directly (no success/data wrapper)
    const data = response.data;
    if (data.access_token) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      setAuthToken(data.access_token);
      setTokenExpiry(data.expires_in);
      setRefreshToken(data.refresh_token);
      syncProfileFromToken(data.access_token);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Token refresh error:', error);
    // Only clear tokens when backend explicitly rejects the refresh token (401/403)
    // Network errors, timeouts, 500s → keep refresh token for retry
    const status = (error as any)?.response?.status;
    if (status === 401 || status === 403) {
      clearRefreshToken();
      clearTokenExpiry();
    } else {
      // Network/CORS/timeout error — set cooldown to prevent request storm
      lastRefreshNetworkFailTime = Date.now();
    }
    return false;
  }
}

/**
 * Makes an API request with auth, retry on 401, and error handling.
 * @param action - Request config (method, url, data, auth, baseUrlKey, etc.)
 * @returns Promise resolving to response data or error
 */
export async function APIRequest(action: any): Promise<any> {
  const {
    method,
    url,
    data,
    notification = true,
    auth = true,
    withFormData,
    baseURL,
    baseUrlKey,
  } = action;

  const makeRequest = async (isRetry = false): Promise<any> => {
    // Check if token is expiring soon before making the request
    // Use shared ensureTokenRefreshed to avoid race conditions with axios interceptor
    if (auth && !isRetry && isTokenExpiringSoon() && getRefreshToken()) {
      try {
        await ensureTokenRefreshed();
      } catch (error) {
        console.error('Failed to refresh token proactively:', error);
      }
    }

    const resolvedBaseUrl = resolveBaseUrl(
      baseURL,
      baseUrlKey as ApiBaseUrlKey,
      apiConfig.API_BASE_URL
    );
    const reqURL = `${resolvedBaseUrl}${url}`;
    let token = null;
    let header: any = {
      Accept: 'application/json',
    };

    if (withFormData) {
      header = Object.assign(header, {
        'Accept-Language': 'th',
      });
    } else {
      header = Object.assign(header, {
        'Content-Type': 'application/json',
        'Accept-Language': 'th',
      });
    }

    // Authorization header removed — browser sends HttpOnly cookie via credentials: 'include'

    let httpResponse: any;

    try {
      const response = await fetch(reqURL, {
        method,
        headers: header,
        body: withFormData ? data : JSON.stringify(data),
        credentials: 'include', // send HttpOnly cookies on cross-origin requests
      });

      httpResponse = response.status;
      const res = await response.json();

      if (res) {
        const errorResult = handleError(httpResponse, res, method);

        // Check if we should try to refresh token
        if (errorResult && errorResult._shouldRefreshToken && !isRetry) {
          // Use shared lock to avoid concurrent refresh with axios interceptor
          let refreshSuccess = false;
          try {
            refreshSuccess = await ensureTokenRefreshed();
          } catch (err) {
            console.error('Failed to refresh token:', err);
          }

          if (refreshSuccess) {
            // Retry the original request with new token
            return makeRequest(true);
          } else if (!getRefreshToken()) {
            // Refresh token was cleared by backend rejection → truly expired
            AlertCustom({
              type: 'error',
              title: 'ERROR',
              message:
                errorResult.originalResponse.message ||
                'Session expired. Please login again.',
              showBtnCancel: false,
            }).then((result) => {
              if (result.isConfirmed) {
                isForceClearLogout();
              }
            });
            return errorResult.originalResponse;
          } else {
            // Network error but refresh token still valid → don't logout
            return errorResult.originalResponse;
          }
        }
      }

      return res;
    } catch (error) {
      console.log('error : ', error);
      console.error('report error : ', error);
      if (notification) {
        // toastr.error(error.message || 'เกิดข้อผิดพลาด');
      }
      return error;
    }
  };

  return makeRequest();
}

export { handleError };
